// The user's Jupnet key: an ed25519 keypair the demo holds in this browser's storage, generated
// here or imported (base58 secret, or a Solana-CLI JSON array). It is the Jupnet account (escrow
// owner, refund payee, GumDeposit recipient) and signs Jupnet opens directly — no wallet in
// between to rewrite the transaction. A demo convenience: the secret sits in localStorage.

import { Keypair } from "@solana/web3.js";
import { base58 } from "./wire";

const STORE = "gum-intent-demo:jupnet-key";
const B58 = "123456789ABCDEFGHJKLMNPQRSTUVWXYZabcdefghijkmnopqrstuvwxyz";

type Storage = Pick<globalThis.Storage, "getItem" | "setItem" | "removeItem">;

export function fromBase58(s: string): Uint8Array {
  let n = 0n;
  for (const c of s) {
    const d = B58.indexOf(c);
    if (d < 0) throw new Error(`not base58: ${JSON.stringify(c)}`);
    n = n * 58n + BigInt(d);
  }
  const bytes: number[] = [];
  while (n > 0n) {
    bytes.unshift(Number(n & 0xffn));
    n >>= 8n;
  }
  for (const c of s) {
    if (c !== "1") break;
    bytes.unshift(0);
  }
  return Uint8Array.from(bytes);
}

const toHex = (b: Uint8Array) => Array.from(b, (x) => x.toString(16).padStart(2, "0")).join("");

/** A secret as a wallet or the CLI exports it: base58 of the 64-byte keypair, or a JSON array of
 *  those bytes; its public half must be the one its secret derives. Never 32 bytes: that is what a
 *  public address is too, and a key seeded with a pasted address is a key anyone can derive. */
export function importJupnetKey(text: string): Keypair {
  const t = text.trim();
  const bytes = t.startsWith("[") ? jsonBytes(t) : fromBase58(t);
  if (bytes.length === 32) throw new Error("that is 32 bytes — a public address, or a bare seed; import the 64-byte keypair (base58, or the JSON array the CLI writes)");
  if (bytes.length !== 64) throw new Error(`a Jupnet key is a 64-byte keypair, not ${bytes.length} bytes`);
  try {
    return Keypair.fromSecretKey(bytes);
  } catch {
    throw new Error("its public half is not the one its secret derives: not a keypair");
  }
}

/** A JSON array of bytes, each a whole number 0–255: `Uint8Array.from` would wrap 256 to 0 or
 *  -1 to 255 and silently import another key. */
function jsonBytes(t: string): Uint8Array {
  const v: unknown = JSON.parse(t);
  if (!Array.isArray(v) || !v.every((b) => Number.isInteger(b) && b >= 0 && b <= 255)) throw new Error("a JSON key is an array of bytes (whole numbers 0 to 255)");
  return Uint8Array.from(v as number[]);
}

/** The secret in base58 (64-byte keypair), for backing it up or moving it to another browser. */
export const exportJupnetKey = (k: Keypair) => base58(toHex(k.secretKey));

/** The stored key, or null — also when storage is unavailable (disabled, a private window): the
 *  demo then starts without one rather than failing to render. */
export function loadJupnetKey(store?: Storage): Keypair | null {
  let s: string | null;
  try {
    s = (store ?? localStorage).getItem(STORE);
  } catch {
    return null;
  }
  if (!s) return null;
  try {
    return importJupnetKey(s);
  } catch {
    return null;
  }
}

/** Throws when storage refuses it (full, disabled): the caller says so — the key would be gone on reload. */
export function saveJupnetKey(k: Keypair, store?: Storage): void {
  (store ?? localStorage).setItem(STORE, exportJupnetKey(k));
}

export function forgetJupnetKey(store?: Storage): void {
  (store ?? localStorage).removeItem(STORE);
}

/** Another tab stored a different key since this one loaded: nothing was overwritten. */
export class StoredKeyChanged extends Error {}

/**
 * Store `next` (or forget the key, `null`) in place of `held`, the key this tab holds — but never
 * over a different key another tab stored meanwhile: that key may own escrows and refunds on
 * Jupnet, and overwriting it loses them on reload. Throws [`StoredKeyChanged`] then (the caller
 * loads the stored key); throws as `saveJupnetKey` does when storage refuses the write.
 */
export function replaceJupnetKey(next: Keypair | null, held: Keypair | null, store?: Storage): void {
  const s = store ?? localStorage;
  let stored: string | null = null;
  try {
    stored = s.getItem(STORE);
  } catch {
    // unreadable storage: the write below fails and says so
  }
  const as = (k: Keypair | null) => (k ? exportJupnetKey(k) : null);
  if (stored !== null && stored !== as(held) && stored !== as(next)) {
    let other = "another key";
    try {
      other = importJupnetKey(stored).publicKey.toBase58();
    } catch {
      // unreadable: named generically
    }
    throw new StoredKeyChanged(`another tab stored a different Jupnet key (${other}); nothing was overwritten`);
  }
  if (next) saveJupnetKey(next, s);
  else forgetJupnetKey(s);
}

/** What a tab holds once another tab changed the stored key to `stored` (null: forgotten or
 *  cleared): that key — unless the one held was never stored (storage refused it), which is then
 *  its only copy: it stays until the user backs it up or forgets it here. */
export function keyAfterStorageChange(held: Keypair | null, heldStored: boolean, stored: Keypair | null): Keypair | null {
  return held && !heldStored ? held : stored;
}

type Events = Pick<EventTarget, "addEventListener" | "removeEventListener">;

/** Call `changed` when another tab stores, replaces or forgets the key (or clears storage), so
 *  every tab holds the stored key. Returns the unsubscribe. */
export function onJupnetKeyChange(changed: () => void, target: Events = globalThis as unknown as Events): () => void {
  const listener = (e: Event) => {
    const key = (e as StorageEvent).key;
    if (key === STORE || key === null) changed();
  };
  target.addEventListener("storage", listener);
  return () => target.removeEventListener("storage", listener);
}

/** What holding `next` came to: the key now held, whether it is in storage, and a warning to keep
 *  showing when it is not the one asked for or lives only in this tab. */
export type Adopted = { key: Keypair | null; stored: boolean; warning: string | null };

/** Hold `next` (a new or imported key, or null to forget) in place of `held`: stored when storage
 *  takes it; never over another tab's key (that one is held instead, with a warning to back it up). */
export function adoptJupnetKey(next: Keypair | null, held: Keypair | null, store?: Storage): Adopted {
  try {
    replaceJupnetKey(next, held, store);
  } catch (e) {
    if (e instanceof StoredKeyChanged) return { key: loadJupnetKey(store), stored: true, warning: `${e.message}: it is the key here now — back it up.` };
    return { key: next, stored: false, warning: `This browser would not store the Jupnet key (${(e as Error).message}). Back it up now — it is gone when the page reloads.` };
  }
  return { key: next, stored: true, warning: null };
}
